import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Anchor, Bell, ChevronDown, Crown, Dices, Gem, Gift,
  Headphones, Home, LayoutGrid, Megaphone, Menu, MessageCircle,
  MessageSquare, Phone,
  PlayCircle, Plus, Rocket, Search, Send, ShieldCheck, Star,
  Target, Ticket, TicketPercent, Trophy, User, Wallet, X, Zap,
} from 'lucide-react';
import Logo from '../../components/Logo';
import UserDropdown from '../../components/UserDropdown';
import AuthModal from '../../components/AuthModal';
import CallbackModal from '../../components/CallbackModal';
import GameSearch from '../../components/GameSearch';
import GameModal from '../../components/GameModal';
import GameChoiceModal from '../../components/GameChoiceModal';
import HerkulFooter from './HerkulFooter';
import HerkulChat from './HerkulChat';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { useGameLauncher } from '../../hooks/useGameLauncher';
import { buildLiveChatConfig, openLiveChat } from '../../components/liveChatConfig';
import { useHiddenCategories } from '../../lib/gameCategories';
import {
  bottomNavIcon, pathOf, splitBottomNav, useBottomNavItems,
} from '../../lib/bottomNav';
import { useTopNavLinks } from '../../lib/topNav';
import { renderSettingText } from '../../lib/textTemplate';
import { resolveApiAssetUrl } from '../../lib/api';
import './herkul.css';
import { adminShortcutVisible } from '../../lib/adminDomain';
// Panelden yazılan CSS'in tutunacağı sabit kimlikler (bkz. lib/domHooks.js):
// kimlik şemaya gömülmez, ortak sabitten okunur.
import { SHELL_IDS } from '../../lib/domHooks';

// Herkül kabuğu üç sütundur: **240px sol ray | içerik | 320px sohbet rayı**.
// Üst çubuk üç sütunun üstünü boydan boya kaplar, içerik kendi içinde kayar
// (sayfa gövdesi kaymaz) — sol ray ve sohbet her zaman yerinde durur.
//
// Sohbet rayı yalnızca bu şemadadır; kabuk onu kendi çizer, ortak bileşenlere
// sızmaz.

// Sol rayın tepesindeki 2×3 renkli kart ızgarası. Referansın imzası altı
// karttır; her kart **gerçek bir sayfaya** bağlanır — karşılığı olmayan bir
// özelliğe (turnuva, mobil uygulama) kart açmak, tıklandığında hiçbir yere
// gitmeyen bir düğme bırakırdı.
const PROMO_CARDS = [
  {
    key: 'firsat', to: '/promosyonlar', label: 'ÖZEL\nFIRSATLAR', icon: Star,
    from: '#8b46f7', to2: '#4c1d95',
  },
  {
    key: 'bonus', to: '/promosyonlar', label: 'BONUS\nTALEP', icon: Gift,
    from: '#16a34a', to2: '#065f46',
  },
  {
    key: 'cark', to: '/cark', label: 'ŞANS\nÇARKI', icon: Target,
    from: '#f59e0b', to2: '#b45309', flag: 'wheel_enabled',
  },
  {
    key: 'capa', to: '/capa', label: 'ÇAPA\nÖDÜLÜ', icon: Anchor,
    from: '#e11d48', to2: '#7f1d1d', flag: 'anchor_enabled',
  },
  {
    key: 'vip', to: '/promosyonlar', label: 'VIP\nKULÜP', icon: Gem,
    from: '#fbbf24', to2: '#a16207',
  },
  {
    key: 'hizli', to: '/crash', label: 'HIZLI\nOYUNLAR', icon: Zap,
    from: '#2563eb', to2: '#1e3a8a', category: 'crash',
  },
];

// Sol rayın birinci menü grubu — oyun kategorileri.
const NAV_PRIMARY = [
  { to: '/', label: 'Ana Sayfa', icon: Home, end: true },
  { to: '/slotlar', label: 'Casino', icon: Dices, category: 'slot' },
  { to: '/canli-casino', label: 'Canlı Casino', icon: PlayCircle, category: 'live-casino' },
  { to: '/crash', label: 'Hızlı Oyunlar', icon: Rocket, category: 'crash' },
  { to: '/cark', label: 'Şans Çarkı', icon: Target, flag: 'wheel_enabled', optional: true },
  { to: '/capa', label: 'Çapa Ödülü', icon: Anchor, flag: 'anchor_enabled', optional: true },
  // Çekiliş kategori değildir; yayında bir çekiliş varken görünür
  // (``raffle_active`` çalışma anındaki durumdur, bkz. CLAUDE.md).
  { to: '/cekilis', label: 'Çekiliş', icon: Ticket, flag: 'raffle_active', optional: true },
  { to: '/etkinlik', label: 'Tahmin', icon: Target, flag: 'prediction_active', optional: true },
];

// İkinci grup — katalog ve kampanya kısayolları.
const NAV_SECONDARY = [
  { to: '/slotlar', label: 'Sağlayıcılar', icon: LayoutGrid },
  { to: '/slotlar', label: 'Özel Listeler', icon: Star, badge: 'NEW' },
  { to: '/promosyonlar', label: 'Promosyonlar', icon: Gift },
  { to: '/promosyon-kodu', label: 'Promo Kod', icon: TicketPercent },
];

const HerkulLayout = ({ children }) => {
  const { user, balance, bonusBalance } = useAuth();
  const settings = useSettings();
  // Panel başka bir alan adına taşındıysa kısayol çizilmez: adresi
  // bilmediğimiz için bağlantı kurulamaz ve panelin varlığını
  // duyurmanın da anlamı yoktur (bkz. lib/adminDomain.js).
  const showAdminLink = adminShortcutVisible(settings);
  const nav = useNavigate();
  const location = useLocation();
  const launcher = useGameLauncher();

  const [drawerOpen, setDrawerOpen] = useState(false);

  // Sol ray daraltılabilir; tercih oyuncunun tarayıcısında kalır (sohbet
  // rayıyla aynı yaklaşım). Daraltılmış rayda yalnızca ikonlar durur.
  const [railMini, setRailMini] = useState(() => {
    try {
      return localStorage.getItem('herkul:rail') === 'mini';
    } catch {
      return false;
    }
  });
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState('login');
  const [callbackOpen, setCallbackOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [noticeClosed, setNoticeClosed] = useState(false);
  const [unread, setUnread] = useState(0);
  // Sohbet rayı gizlenebilir; tercih tarayıcıda kalır, sayfa yenilenince
  // oyuncunun kapattığı ray geri açılmamalıdır.
  const [chatOpen, setChatOpen] = useState(() => {
    try {
      return localStorage.getItem('herkul:chat') !== 'off';
    } catch {
      return true;
    }
  });
  const hiddenCats = useHiddenCategories();

  const callbackEnabled = settings.callback_enabled !== false;
  const liveChatEnabled = useMemo(
    () => Boolean(buildLiveChatConfig(settings, user)),
    [settings, user],
  );

  // Hamburger iki iş yapar: sol rayın hiç çizilmediği dar ekranda (960px altı)
  // çekmeceyi açar, masaüstünde rayı daraltıp genişletir. Tek düğme, çünkü
  // ikisi de "menü" işidir; ayrı bir daraltma düğmesi aynı köşede iki menü
  // düğmesi gösterirdi.
  const toggleRail = useCallback(() => {
    const mobile = typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(max-width: 960px)').matches;
    if (mobile) {
      setDrawerOpen(true);
      return;
    }
    setRailMini((mini) => {
      const next = !mini;
      try { localStorage.setItem('herkul:rail', next ? 'mini' : 'full'); } catch { /* yoksay */ }
      return next;
    });
  }, []);

  // Sohbet rayı 1400px altında sığmaz; orada sohbet tam ekran bir sayfa
  // olarak açılır ve onu küçük bir yüzer düğme çağırır. Hangi kabuğun
  // çizileceği CSS ile değil **burada** belirlenir: iki kabuk birden
  // bağlansaydı akış iki kez yoklanır, dar ekranda görünmeyen ray da boşuna
  // istek atardı (eskiden öyleydi — ray yalnızca CSS ile gizleniyordu).
  const [narrow, setNarrow] = useState(() => (
    typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(max-width: 1400px)').matches
  ));
  const [chatSheet, setChatSheet] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia('(max-width: 1400px)');
    const onChange = (event) => setNarrow(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  // Sayfa açıkken arkadaki içerik kaymamalı; çekmecedeki kuralın aynısı.
  useEffect(() => {
    if (!chatSheet) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [chatSheet]);

  // Gezinince sayfa kapanır: sohbet açıkken bir bağlantıya basan oyuncu
  // gittiği sayfayı göremezdi.
  useEffect(() => { setChatSheet(false); }, [location.pathname]);

  const toggleChat = useCallback(() => {
    setChatOpen((open) => {
      const next = !open;
      try { localStorage.setItem('herkul:chat', next ? 'on' : 'off'); } catch { /* yoksay */ }
      return next;
    });
  }, []);

  // Zil rozetindeki sayı gerçek okunmamış mesaj sayısıdır; üye yokken
  // istek hiç atılmaz.
  useEffect(() => {
    if (!user) { setUnread(0); return undefined; }
    let alive = true;
    api.get('/messages', { params: { limit: 1 } })
      .then(({ data }) => { if (alive) setUnread(Number(data?.unread) || 0); })
      .catch(() => { /* zil rozeti yan bilgidir, hata basılmaz */ });
    return () => { alive = false; };
  }, [user, location.pathname]);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('reset_token')) {
      setAuthTab('reset');
      setAuthOpen(true);
    }
  }, []);

  useEffect(() => {
    if (location.state?.auth !== 'login' || user) return;
    setAuthTab('login');
    setAuthOpen(true);
    nav(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, nav, user]);

  // Sayfa değişince mobil çekmece kapanır.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Çekmece açıkken arkadaki sayfa kaymasın.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [drawerOpen]);

  const extraNav = useTopNavLinks();

  const shortcutPaths = useMemo(() => new Set(
    extraNav.filter((item) => !item.external).map((item) => pathOf(item.to)),
  ), [extraNav]);

  // Bayraklar tek tek okunur: ``settings`` nesnesinin tamamını bağımlılığa
  // koymak menüyü her renderda yeniden üretirdi — context değeri her
  // yoklamada yeni bir nesnedir.
  const flagOn = useCallback((flag) => {
    if (flag === 'wheel_enabled') return settings.wheel_enabled === true;
    if (flag === 'anchor_enabled') return settings.anchor_enabled === true;
    if (flag === 'raffle_active') return settings.raffle_active === true;
    if (flag === 'prediction_active') return settings.prediction_active === true;
    return true;
  }, [settings.wheel_enabled, settings.anchor_enabled, settings.raffle_active, settings.prediction_active]);

  const visibleNav = useMemo(() => NAV_PRIMARY.filter((item) => {
    if (item.optional && shortcutPaths.has(item.to)) return false;
    if (item.flag) return flagOn(item.flag);
    return !item.category || !hiddenCats.includes(item.category);
  }), [hiddenCats, shortcutPaths, flagOn]);

  // Kapalı bir bölüme götüren kart çizilmez — ızgara altı gözlüdür ama
  // tıklandığında kapalı sayfaya düşen bir kart, bozuk bir site izlenimi
  // bırakır.
  const promoCards = useMemo(() => PROMO_CARDS.filter((card) => {
    if (card.flag && !flagOn(card.flag)) return false;
    return !card.category || !hiddenCats.includes(card.category);
  }), [flagOn, hiddenCats]);

  const secondaryNav = useMemo(() => NAV_SECONDARY.filter(
    (item) => !item.category || !hiddenCats.includes(item.category),
  ), [hiddenCats]);

  // Spor bölümü açık mı? Üst çubuktaki Casino/Spor sekmesi buna göre çizilir.
  const sportsEnabled = settings.settingsLoaded ? settings.sports_enabled !== false : true;
  const onSports = location.pathname.startsWith('/spor');

  const bottomNav = useBottomNavItems();
  const { left: bottomLeft, right: bottomRight } = splitBottomNav(bottomNav);

  const openAuth = useCallback((tab) => { setAuthTab(tab); setAuthOpen(true); }, []);

  const renderBottomItem = (item, index) => {
    const Icon = bottomNavIcon(item.icon);
    const key = `${item.type}-${item.url}-${index}`;
    if (item.type === 'menu') {
      return (
        <button
          key={key}
          type="button"
          className={`herkul-bottom-item ${drawerOpen ? 'active' : ''}`}
          onClick={() => setDrawerOpen((open) => !open)}
          aria-expanded={drawerOpen}
        >
          <Icon size={18} />
          <span>{item.label}</span>
        </button>
      );
    }
    return (
      <NavLink
        key={key}
        to={item.url}
        end={item.url === '/'}
        className={({ isActive }) => `herkul-bottom-item ${isActive ? 'active' : ''}`}
      >
        <Icon size={18} />
        <span>{item.label}</span>
      </NavLink>
    );
  };

  const announcement = renderSettingText(settings.top_bar_message || '', settings).trim();

  // Alt bardaki merkez düğmesinde site logosu/faviconu gösterilir (kare olan
  // favicon tercih edilir); ikisi de yoksa bir ikona düşülür.
  const brandMark = settings.favicon_url || settings.logo_image_url || '';

  // Dikey menü maddesi (sol ray + mobil çekmece ortak). `title` daraltılmış
  // rayda zorunludur: orada yazı gizlenir ve ikonun ne olduğu başka türlü
  // okunamaz.
  const renderMenuItem = (item) => (item.external ? (
    <a
      key={`${item.to}-${item.label}`}
      href={item.to}
      target={item.newTab ? '_blank' : undefined}
      rel={item.newTab ? 'noreferrer' : undefined}
      className="herkul-menu-item"
      title={item.label}
    >
      <item.icon size={18} />
      <span>{item.label}</span>
      {item.badge && <span className="herkul-menu-badge">{item.badge}</span>}
    </a>
  ) : (
    <NavLink
      key={`${item.to}-${item.label}`}
      to={item.to}
      end={item.end}
      target={item.newTab ? '_blank' : undefined}
      className={({ isActive }) => `herkul-menu-item ${isActive ? 'active' : ''}`}
      title={item.label}
    >
      <item.icon size={18} />
      <span>{item.label}</span>
      {item.badge && <span className="herkul-menu-badge">{item.badge}</span>}
    </NavLink>
  ));

  // Sol rayın gövdesi (masaüstü sabit ray ile mobil çekmece aynı içeriği
  // paylaşır).
  const sidebarBody = (
    <>
      {promoCards.length > 0 && (
        <div className="herkul-promo-grid">
          {promoCards.map((card) => (
            <NavLink
              key={card.key}
              to={card.to}
              className="herkul-promo-card"
              title={card.label.split('\n').join(' ')}
              style={{ '--pc-from': card.from, '--pc-to': card.to2 }}
            >
              <span className="herkul-promo-icon"><card.icon size={22} /></span>
              <span className="herkul-promo-label">
                {card.label.split('\n').map((line) => <span key={line}>{line}</span>)}
              </span>
            </NavLink>
          ))}
        </div>
      )}

      <nav className="herkul-menu" aria-label="Ana menü">
        {visibleNav.map(renderMenuItem)}

        <div className="herkul-menu-rule" />
        {secondaryNav.map(renderMenuItem)}
        {liveChatEnabled && (
          <button
            type="button"
            className="herkul-menu-item is-support"
            title="Canlı Destek"
            onClick={() => {
              setDrawerOpen(false);
              openLiveChat();
            }}
          >
            <Headphones size={18} />
            <span>Canlı Destek</span>
          </button>
        )}
        {callbackEnabled && (
          <button
            type="button"
            className="herkul-menu-item"
            title="Beni Ara"
            onClick={() => { setDrawerOpen(false); setCallbackOpen(true); }}
          >
            <Phone size={18} />
            <span>Beni Ara</span>
          </button>
        )}

        {extraNav.length > 0 && <div className="herkul-menu-rule" />}
        {extraNav.map(renderMenuItem)}

        {(user || (user?.is_admin && showAdminLink)) && <div className="herkul-menu-rule" />}
        {user && (
          <NavLink to="/hesap" className="herkul-menu-item" title="Hesabım">
            <User size={18} />
            <span>Hesabım</span>
          </NavLink>
        )}
        {user?.is_admin && showAdminLink && (
          <NavLink to="/admin" className="herkul-menu-item" title="Yönetim Paneli">
            <ShieldCheck size={18} />
            <span>Yönetim Paneli</span>
          </NavLink>
        )}
      </nav>

      {/* Rayın dibindeki tanıtım kartı — referansın "VIP Program" kartı. */}
      <div className="herkul-side-card">
        <p className="herkul-side-card-title"><Crown size={15} /> Kampanyalar</p>
        <p className="herkul-side-card-text">
          Yatırım bonusları, freespin hakları ve çevrimsiz ödüller seni bekliyor.
        </p>
        <NavLink to="/promosyonlar" className="herkul-side-card-btn">Fırsatları Gör</NavLink>
      </div>
    </>
  );

  return (
    <div className={`herkul-shell ${chatOpen ? 'has-chat' : ''} ${railMini ? 'is-rail-mini' : ''}`}>
      <header id={SHELL_IDS.header} className="herkul-header">
        <div className="herkul-header-left">
          <button
            type="button"
            className="herkul-burger"
            onClick={toggleRail}
            aria-label="Menü"
            aria-expanded={!railMini}
          >
            <Menu size={20} />
          </button>

          {/* Casino / Spor geçiş sekmeleri — referans arayüzün sol üst imzası. */}
          <div className="herkul-mode-toggle">
            <NavLink
              to="/"
              end
              className={`herkul-mode ${!onSports ? 'active' : ''}`}
            >
              <Dices size={15} />
              <span>Casino</span>
            </NavLink>
            {sportsEnabled && (
              <NavLink
                to="/spor"
                className={`herkul-mode ${onSports ? 'active' : ''}`}
              >
                <Trophy size={15} />
                <span>Spor</span>
              </NavLink>
            )}
          </div>
        </div>

        <div className="herkul-header-brand">
          <Logo />
          {settings.telegram_url && (
            <a
              href={settings.telegram_url}
              target="_blank"
              rel="noreferrer"
              className="herkul-round-btn is-telegram"
              aria-label="Telegram"
            >
              <Send size={15} />
            </a>
          )}
          {liveChatEnabled && (
            <button
              type="button"
              className="herkul-round-btn"
              onClick={() => openLiveChat()}
              aria-label="Canlı Destek"
            >
              <Headphones size={15} />
            </button>
          )}
        </div>

        <div className="herkul-header-actions">
          {user ? (
            <>
              <button
                type="button"
                className="herkul-btn herkul-btn-accent herkul-btn-deposit"
                onClick={() => nav('/hesap/para-yatir')}
                aria-label="Para Yatır"
              >
                <Plus size={16} />
                <span className="herkul-btn-text">Para Yatır</span>
              </button>
              <div className="herkul-balance">
                <Wallet size={15} style={{ color: 'var(--pusu-gold)' }} />
                <span className="herkul-balance-body">
                  <span className="herkul-balance-label">Bakiye</span>
                  <span className="herkul-balance-value">{Number(balance).toFixed(2)} ₺</span>
                </span>
              </div>
              <div className="herkul-balance herkul-balance-bonus">
                <Gift size={15} style={{ color: 'var(--pusu-gold)' }} />
                <span className="herkul-balance-body">
                  <span className="herkul-balance-label">Bonus</span>
                  <span className="herkul-balance-value">{Number(bonusBalance).toFixed(2)} ₺</span>
                </span>
              </div>
            </>
          ) : null}

          <span className="herkul-lang" aria-label="Site dili: Türkçe">
            <span className="herkul-lang-flag">TR</span>
            <ChevronDown size={13} />
          </span>

          <button
            type="button"
            className="herkul-round-btn"
            onClick={() => setSearchOpen(true)}
            aria-label="Hızlı Ara"
          >
            <Search size={15} />
          </button>

          {user && (
            <NavLink to="/hesap/mesajlar" className="herkul-round-btn" aria-label="Bildirimler">
              <Bell size={15} />
              {unread > 0 && <span className="herkul-bell-badge">{unread > 9 ? '9+' : unread}</span>}
            </NavLink>
          )}

          {/* Sohbet rayını gizleyip geri açar; dar ekranda gizlenir. */}
          <button
            type="button"
            className={`herkul-round-btn herkul-chat-toggle ${chatOpen ? 'is-on' : ''}`}
            onClick={toggleChat}
            aria-label={chatOpen ? 'Sohbeti gizle' : 'Sohbeti göster'}
            aria-pressed={chatOpen}
          >
            <MessageCircle size={15} />
          </button>

          {user ? (
            <UserDropdown />
          ) : (
            <>
              <button
                type="button"
                className="herkul-btn herkul-btn-ghost"
                onClick={() => openAuth('login')}
              >
                Giriş Yap
              </button>
              {settings.registration_open !== false && (
                <button
                  type="button"
                  className="herkul-btn herkul-btn-accent"
                  onClick={() => openAuth('register')}
                >
                  Kayıt Ol
                </button>
              )}
            </>
          )}
        </div>
      </header>

      {/* Masaüstü sabit sol ray. */}
      <aside className="herkul-sidebar" aria-label="Kenar menü">
        <div className="herkul-sidebar-scroll">{sidebarBody}</div>
      </aside>

      {/* Mobil çekmece: aynı ray içeriği dar ekranda kayar. */}
      <aside className={`herkul-drawer ${drawerOpen ? 'is-open' : ''}`}>
        <div className="herkul-drawer-head">
          <Logo />
          <button
            type="button"
            className="herkul-drawer-close"
            onClick={() => setDrawerOpen(false)}
            aria-label="Menüyü kapat"
          >
            <X size={18} />
          </button>
        </div>
        <div className="herkul-drawer-scroll">{sidebarBody}</div>
      </aside>
      {drawerOpen && (
        <div
          className="herkul-drawer-backdrop"
          role="presentation"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <div className="herkul-main">
        {announcement && !noticeClosed && (
          <div className="herkul-notice">
            <span className="herkul-notice-icon"><Megaphone size={15} /></span>
            <p className="herkul-notice-text">{announcement}</p>
            <button
              type="button"
              className="herkul-notice-close"
              onClick={() => setNoticeClosed(true)}
              aria-label="Duyuruyu kapat"
            >
              <X size={14} />
            </button>
          </div>
        )}

        <div id={SHELL_IDS.main} className="herkul-content">{children}</div>

        <HerkulFooter />
      </div>

      {/* Sohbet — yalnızca bu şemada. Geniş ekranda sağdaki ray, dar ekranda
          tam ekran sayfa; ikisi aynı anda çizilmez. */}
      {(narrow ? chatSheet : chatOpen) && (
        <HerkulChat
          sheet={narrow}
          onCollapse={narrow ? () => setChatSheet(false) : toggleChat}
          onRequireLogin={() => openAuth('login')}
        />
      )}
      {/* Sohbet kapalıyken sağ kenarda duran altın sekme. Referans arayüzde
          bu sekme zeminden ayrışan tek altın öğedir: sohbet gizlenince onu
          geri açacak tek kapı odur ve kart ızgarasının içinde kaybolmamalıdır.
          İkon konuşma balonudur — ok işareti "kaydır" anlamına geliyordu. */}
      {!narrow && !chatOpen && (
        <button
          type="button"
          className="herkul-chat-peek"
          onClick={toggleChat}
          aria-label="Sohbeti göster"
        >
          <MessageSquare size={17} />
          <span>Sohbet</span>
        </button>
      )}

      {/* Dar ekranda sohbeti çağıran küçük düğme. Alt barın üstünde durur;
          barın içine bir yuva olarak konsaydı panelden yönetilen dört yuvadan
          birini yer ve dört şemanın ortak dosyasını değiştirmek gerekirdi. */}
      {narrow && !chatSheet && (
        <button
          type="button"
          className="herkul-chat-fab"
          onClick={() => setChatSheet(true)}
          aria-label="Sohbeti aç"
        >
          <MessageSquare size={19} />
        </button>
      )}

      {/* Mobil alt bar: panelden gelen dört yuva + ortadaki sabit düğme. */}
      <nav id={SHELL_IDS.bottomBar} className="app-bottom-bar herkul-bottom-bar" aria-label="Alt menü">
        {bottomLeft.map(renderBottomItem)}
        {/* Ortadaki yükseltilmiş marka butonu (referansın imzası): altın halka
            içinde site logosu. İçerik panelden yönetilir, bu düğme sabit kalır
            ve ana sayfaya gider. */}
        <NavLink to="/" end className="herkul-bottom-center" aria-label="Ana Sayfa">
          <span className="herkul-bottom-center-ring">
            {brandMark ? (
              <img src={resolveApiAssetUrl(brandMark)} alt="" />
            ) : (
              <Dices size={22} />
            )}
          </span>
        </NavLink>
        {bottomRight.map(renderBottomItem)}
      </nav>

      {searchOpen && (
        <div
          className="fixed inset-0 z-[70] flex justify-center px-4 pt-24"
          style={{ background: 'rgba(1, 14, 15, 0.82)' }}
          onClick={() => setSearchOpen(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-2xl"
            onClick={(event) => event.stopPropagation()}
            role="presentation"
          >
            <GameSearch onPlay={(game) => { setSearchOpen(false); launcher.play(game); }} />
          </div>
        </div>
      )}

      <GameChoiceModal
        game={launcher.choice}
        user={launcher.user}
        balance={launcher.balance}
        bonusBalance={launcher.bonusBalance}
        demoAvailable={launcher.demoAvailable}
        onClose={launcher.closeChoice}
        onReal={launcher.startReal}
        onDemo={launcher.startDemo}
        onLogin={() => launcher.openAuth('login')}
        onRegister={() => launcher.openAuth('register')}
      />
      {launcher.activeGame && (
        <GameModal
          key={`${launcher.activeGame.id}-${launcher.mode}`}
          game={launcher.activeGame}
          mode={launcher.mode}
          onClose={launcher.closeGame}
        />
      )}

      <AuthModal
        open={authOpen || !!launcher.authTab}
        onClose={() => { setAuthOpen(false); launcher.setAuthTab(null); }}
        defaultTab={launcher.authTab || authTab}
        registrationOpen={settings.registration_open !== false}
      />
      <CallbackModal open={callbackEnabled && callbackOpen} onClose={() => setCallbackOpen(false)} />
    </div>
  );
};

export default HerkulLayout;
